import {
  ExternalLink,
  Globe,
  GraduationCap,
  Library,
  Scale,
} from "lucide-react";
import React from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";

interface LinksListProps {
  loading?: boolean;
}

const LinksList: React.FC<LinksListProps> = ({ loading }) => {
  const linkCategories = [
    {
      title: "Recursos Oficiales",
      description: "Fuentes oficiales de información legal en España",
      icon: <Scale className="h-5 w-5 text-primary" />,
      links: [
        {
          title: "Boletín Oficial del Estado (BOE)",
          url: "https://www.boe.es/",
          description: "Portal oficial del Boletín Oficial del Estado",
        },
        {
          title: "Agencia Estatal Boletín Oficial del Estado",
          url: "https://www.boe.es/aeboe/",
          description: "Información sobre la Agencia Estatal BOE",
        },
        {
          title: "Portal de la Administración Electrónica",
          url: "https://administracionelectronica.gob.es/",
          description: "Recursos sobre administración digital en España",
        },
      ],
    },
    {
      title: "Recursos Jurídicos",
      description:
        "Bases de datos y herramientas para profesionales del Derecho",
      icon: <Library className="h-5 w-5 text-primary" />,
      links: [
        {
          title: "Consejo General del Poder Judicial",
          url: "https://www.poderjudicial.es/",
          description: "Jurisprudencia y recursos judiciales",
        },
        {
          title: "Noticias Jurídicas",
          url: "https://noticias.juridicas.com/",
          description: "Portal de información jurídica actualizada",
        },
        {
          title: "vLex España",
          url: "https://vlex.es/",
          description:
            "Base de datos jurídica con jurisprudencia y legislación",
        },
      ],
    },
    {
      title: "Recursos Académicos",
      description: "Fuentes de información para investigación y estudio",
      icon: <GraduationCap className="h-5 w-5 text-primary" />,
      links: [
        {
          title: "Dialnet",
          url: "https://dialnet.unirioja.es/",
          description: "Portal bibliográfico de artículos científicos",
        },
        {
          title: "CENDOJ",
          url: "https://www.poderjudicial.es/search/indexAN.jsp",
          description: "Centro de Documentación Judicial",
        },
        {
          title: "Iberley",
          url: "https://www.iberley.es/",
          description: "Portal de información jurídica",
        },
      ],
    },
    {
      title: "Recursos Internacionales",
      description: "Fuentes de información legal internacional",
      icon: <Globe className="h-5 w-5 text-primary" />,
      links: [
        {
          title: "EUR-Lex",
          url: "https://eur-lex.europa.eu/",
          description: "Portal de Derecho de la Unión Europea",
        },
        {
          title: "DOUE - Diario Oficial de la Unión Europea",
          url: "https://eur-lex.europa.eu/oj/direct-access.html",
          description:
            "Publicación oficial de la legislación y otros actos de la UE",
        },
        {
          title: "Tribunal de Justicia de la Unión Europea",
          url: "https://curia.europa.eu/",
          description: "Jurisprudencia del TJUE",
        },
      ],
    },
  ];

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
        <p className="text-muted-foreground">Cargando enlaces...</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold mb-2">Enlaces de Interés</h2>
        <p className="text-muted-foreground">
          Esta sección proporciona acceso directo a recursos oficiales, bases de
          datos jurídicas y otras fuentes de información relevantes para
          complementar la consulta del BOE. Estos enlaces han sido seleccionados
          para facilitar el acceso a información legal y administrativa de
          calidad.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {linkCategories.map((category, i) => (
          <Card
            key={i}
            className="border shadow-sm hover:shadow-md transition-shadow"
          >
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                {category.icon}
                <CardTitle>{category.title}</CardTitle>
              </div>
              <CardDescription>{category.description}</CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3">
                {category.links.map((link, j) => (
                  <li key={j} className="group">
                    <a
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-start gap-2 p-2 rounded-md hover:bg-secondary/50 transition-colors"
                    >
                      <ExternalLink className="h-4 w-4 mt-1 flex-shrink-0 text-muted-foreground group-hover:text-primary transition-colors" />
                      <div>
                        <div className="font-medium group-hover:text-primary transition-colors">
                          {link.title}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {link.description}
                        </div>
                      </div>
                    </a>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default LinksList;
